/* eslint-disable @nx/enforce-module-boundaries */
import {
  Card,
  Stack,
  HStack,
  Text,
  Spinner,
  VStack,
  Image,
} from '@mybridge/ui';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { SearchJobCard } from 'v4/components/jobs/search-job-card';
import { useRouter } from 'next/router';
import { SearchContext } from '../../context';
import { SearchCardSkeleton } from '../../search-card-skeleton';
import InfiniteScroll from 'react-infinite-scroll-component';
import { JobsListingContext } from 'v4/snippets/jobs/context';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { searchAll } from 'v4/store/actions/global.data.actions';
import { getNextPageFromURL } from 'v4/utils/utils';
import { isMobile } from 'react-device-detect';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const SearchNewJobListing = forwardRef(
  ({ count = 0, listing, isMainList, ...props }, ref) => {
    const {
      activeTab,
      jobsTotalCount,
      search,
      loggedIn,
      jobsSearchListing,
      jobsSearchIsFetching,
    } = useContext(SearchContext);
    const dispatch = useDispatch();
    const { push } = useRouter();
    const { t } = useTranslator();

    const {
      data,
      refetch,
      isFetching,
      fetchNextPage,
      hasNextPage: jobsHasNextPage,
    } = useInfiniteQuery({
      queryKey: ['jobs-search', { search }],
      queryFn: async ({ queryKey, pageParam = 1 }) => {
        try {
          const { search } = queryKey[1];
          const resp = await dispatch(
            searchAll({
              category: 'jobs',
              query: search,
              pageParam,
              isNoToken: !loggedIn
            })
          );
          return resp?.payload ?? {};
        } catch (e) {
          console.error(e);
        }
      },
      getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
      refetchOnWindowFocus: false,
    });

    const {
      isSavedJob,
      isAppliedJob,
      appliedJobsListing,
      saveJob,
      saveJobIsLoading,
      removeSavedJob,
      removeSavedJobIsLoading,
    } = useContext(JobsListingContext);
    
    const [selected, setSelected] = useState();

    useEffect(() => {
      if (!listing && activeTab === 'pages') {
        refetch();
      }
    }, [search, listing, activeTab]);

/*    const jobsList = listing ?? (data?.pages?.flatMap((page) => page?.results) || []); */
    const jobsList = listing ?? (jobsSearchListing?.pages?.flatMap((page) => page?.results) || data?.pages?.flatMap((page) => page?.results) || []);
    const isLoadingState = isFetching || jobsSearchIsFetching;

    return (
      <Stack mx={isMobile&&"auto"} w={isMobile?"96%":"100%"} mt={2} spacing={4}>
        {isLoadingState ? (
          <SearchCardSkeleton />
        ) : isMainList ? (
          jobsList?.map((li, liIndex) => (
            <Card
            flex={1}
            p={4}
            key={liIndex}
            variant="thinBorderedCard"
            mb={3}
            border="1px solid #D4D4D4"
            width= {isMobile?'96%':"70%"}
            height= "80%"
            borderRadius="20px"
            mx={isMobile?"auto":""}
            boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.1),0 1px 2px 0 rgba(0, 0, 0, 0.06)"
          >
              {(() => {
                const saved = isSavedJob(li?.id);
                const applied = isAppliedJob?.(li?.id) || appliedJobsListing?.find?.((aj) => aj?.job_details?.id === li?.id || aj?.id === li?.id || aj?.job?.id === li?.id) || (li?.is_applied ? li : null);
                return (
                  <SearchJobCard
                    href={`/j/${li?.id}`}
                    onApplyClick={() => push('/j/' + li?.id)}
                    isSaved={saved}
                    isApplied={!!applied}
                    appliedData={applied}
                    onSaveClick={() => {
                      setSelected(li);
                      if (saved) {
                        removeSavedJob(saved);
                      } else {
                        saveJob({ job_id: li?.id });
                      }
                    }}
                    saveLoading={
                      selected?.id === li?.id &&
                      (saveJobIsLoading || removeSavedJobIsLoading)
                    }
                    saveDisabled={!!saved}
                    job={li}
                  />
                );
              })()}
            </Card>
          ))
        ) : (
          <>
            {(jobsTotalCount > 0 || jobsList?.length > 0) ? (
              <InfiniteScroll
                dataLength={jobsList?.length}
                next={fetchNextPage}
                hasMore={jobsHasNextPage}
                loader={
                  <HStack p={4} justifyContent="center" w="100%">
                    <Spinner />
                  </HStack>
                }
              >
                {jobsList?.map((li, liIndex) => (
                  <Card
                    flex={1}
                    p={4}
                    key={liIndex}
                    variant="thinBorderedCard"
                    mb={3}
                    border="1px solid #D4D4D4"
                    width={isMobile?"100%":"70%"}
                    height= "80%"
                    borderRadius="20px"
                    boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.1),0 1px 2px 0 rgba(0, 0, 0, 0.06)"
                  >
                    {(() => {
                      const saved = isSavedJob(li?.id);
                      const applied = isAppliedJob?.(li?.id) || appliedJobsListing?.find?.((aj) => aj?.job_details?.id === li?.id || aj?.id === li?.id || aj?.job?.id === li?.id) || (li?.is_applied ? li : null);
                      return (
                        <SearchJobCard
                          href={`/j/${li?.id}`}
                          onApplyClick={() => push('/j/' + li?.id)}
                          isSaved={saved}
                          isApplied={!!applied}
                          appliedData={applied}
                          onSaveClick={() => {
                            setSelected(li);
                            if (saved) {
                              removeSavedJob(saved);
                            } else {
                              saveJob({ job_id: li?.id });
                            }
                          }}
                          saveLoading={
                            selected?.id === li?.id &&
                            (saveJobIsLoading || removeSavedJobIsLoading)
                          }
                          saveDisabled={!!saved}
                          job={li}
                        />
                      );
                    })()}
                  </Card>
                ))}
              </InfiniteScroll>
            ) : (
              <VStack
                justifyContent="center"
                alignItems="center"
                h="calc(80vh - 64px)"
              >
                <Image
                  src="/images/defaultSearchImage.svg"
                  alt="default Image"
                  width={["80%", "400px"]}
                />
                <Text fontSize="18px" color="#AFAFAF" fontWeight="500">
                  {t('searchPage.noResultsFound') || "No results found"}
                </Text>
                <Text fontSize="14px" color="#AFAFAF" fontWeight="500">
                  {t('searchPage.tryRephrasing') || "Try shortening or rephrasing your search."}
                </Text>
              </VStack>
            )}
          </>
        )}
      </Stack>
    );
  }
);
